import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { IMAGES, scrollToHash } from "../config/site";

const EASE = [0.22, 1, 0.36, 1];

const HERO_FRAMES = [IMAGES.hero, IMAGES.heroFilm2, IMAGES.heroFilm3];

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.35 + i * 0.16, ease: EASE },
  }),
};

const Hero = () => {
  const [frame, setFrame] = useState(0);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 180]);
  const fade = useTransform(scrollY, [0, 500], [1, 0]);

  useEffect(() => {
    const id = setInterval(() => setFrame((f) => (f + 1) % HERO_FRAMES.length), 7000);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="top" data-testid="hero-section" className="relative h-[100svh] min-h-[620px] overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-[1.12]">
        {HERO_FRAMES.map((src, i) => (
          <motion.img
            key={src}
            src={src}
            alt="Steaming South Indian filter coffee in a brass davara-tumbler"
            className="absolute inset-0 h-full w-full object-cover"
            initial={false}
            animate={{ opacity: i === frame ? 1 : 0, scale: i === frame ? 1 : 1.05 }}
            transition={{
              opacity: { duration: 2.4, ease: "easeInOut" },
              scale: { duration: 8, ease: "easeInOut" },
            }}
          />
        ))}
      </motion.div>
      <div className="absolute inset-0 bg-black/40" />
      <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-espresso to-transparent" />

      {/* steam */}
      <div className="pointer-events-none absolute left-1/2 top-[38%] hidden md:block" aria-hidden="true">
        <span className="steam-wisp" style={{ left: -30, animationDelay: "0s" }} />
        <span className="steam-wisp" style={{ left: 6, animationDelay: "1.4s" }} />
        <span className="steam-wisp" style={{ left: -64, animationDelay: "2.6s", opacity: 0.6 }} />
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-center px-5 md:px-10"
      >
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
          className="mb-6 text-[11px] uppercase tracking-widest2 text-gold"
        >
          The Modern Village Coffee
        </motion.p>

        <h1 className="max-w-4xl font-tamil text-[2.1rem] leading-[1.18] text-cream sm:text-5xl lg:text-[4.2rem]">
          {["சில நினைவுகள்...", "Coffee மணத்தோடுதான்", "தொடங்கும்."].map((t, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block will-change-transform"
                variants={line}
                custom={i}
                initial="hidden"
                animate="show"
              >
                {t}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
          className="mt-6 max-w-md font-tamil text-base text-cream/75 sm:text-lg"
        >
          மண் மணமும்... மனம் நிறைந்த Coffee சுவையும்.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-explore-coffee-button"
            onClick={() => scrollToHash("#products")}
            className="group relative overflow-hidden rounded-full bg-gold px-8 py-4 text-sm font-medium uppercase tracking-[0.15em] text-espresso transition-transform duration-300 hover:scale-[1.03] focus:outline-none focus:ring-1 focus:ring-gold"
          >
            <span className="relative z-10">Explore Coffee</span>
            <span className="absolute inset-0 -translate-x-full bg-cream transition-transform duration-500 ease-out group-hover:translate-x-0" />
          </button>
          <button
            data-testid="hero-become-dealer-button"
            onClick={() => scrollToHash("#dealer")}
            className="rounded-full border border-cream/40 px-8 py-4 text-sm font-medium uppercase tracking-[0.15em] text-cream transition-all duration-300 hover:border-gold hover:text-gold"
          >
            Become a Dealer
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToHash("#story")}
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-cream/60 transition-colors hover:text-gold"
        aria-label="Scroll to our story"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
      >
        <ArrowDown size={20} />
      </motion.button>
    </section>
  );
};

export default Hero;
